import { createFileRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useCallback, useMemo, useState } from 'react';
import {
	ConfirmDialog,
	EventInfoBox,
	GeneralNotFound,
	GenericDetailPage,
	Spinner,
	Tabs,
} from '@repo/components';
import DuplicateEventDialog from 'src/components/DuplicateEventDialog';
import { PERMISSIONS } from '@repo/shared/domain';
import { EventForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getEvent, getEventApprovals, getEventDocuments } from 'src/api/admin/events';
import { getEventTypes } from 'src/api/admin/types';
import { getProjects } from 'src/api/admin/projects';
import { getAllTags } from 'src/api/admin/tags';

export const Route = createFileRoute('/_admin/events/$eventId')({
	component: EventDetailLayout,
});

function EventDetailLayout() {
	const { eventId } = Route.useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const { value: locallyStoredBackUrl } = useLocalStorage<string>('eventsBackUrl');

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config, loading: configLoading } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesSubEvents = featureFlags?.USES_SUBCONTRACTING || false;
	const usesTags = featureFlags?.USES_TAGS || false;

	const [isFormOpened, setIsFormOpened] = useState(false);
	const [isEventDuplicateDialogOpen, setIsEventDuplicateDialogOpen] = useState(false);
	const [isEventArchiveDialogOpen, setIsEventArchiveDialogOpen] = useState(false);
	const [isExceptionInitiateDialogOpen, setIsExceptionInitiateDialogOpen] = useState(false);
	const [isExceptionAcceptDialogOpen, setIsExceptionAcceptDialogOpen] = useState(false);
	const [isExceptionCancelDialogOpen, setIsExceptionCancelDialogOpen] = useState(false);
	const [isReviewExceptionConfirmDialogOpen, setIsReviewExceptionConfirmDialogOpen] = useState(false);
	const [exceptionReason, setExceptionReason] = useState('');
	const [exceptionAcceptReason, setExceptionAcceptReason] = useState('');
	/** Hides Cancel Exception / Mark reviewed (exception path) while a confirm request runs (header still visible). */
	const [exceptionActionPending, setExceptionActionPending] = useState(false);
	/** Same pattern as user resend-invite: `confirmDisabled` on ConfirmDialog until the request finishes. */
	const [isExceptionConfirmBusy, setIsExceptionConfirmBusy] = useState(false);
	const [isInitiateExceptionSubmitting, setIsInitiateExceptionSubmitting] = useState(false);
	const [isAcceptExceptionSubmitting, setIsAcceptExceptionSubmitting] = useState(false);

	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['event', eventId],
		queryFn: () => getEvent(eventId, 'includeArchived=true'),
	});

	const { data: typesData } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes(),
	});

	const { data: approvalsData } = useQuery({
		queryKey: ['event-approvals', eventId],
		queryFn: () => getEventApprovals(eventId),
		enabled: !!event,
	});

	const { data: documentsData } = useQuery({
		queryKey: ['event-documents', eventId],
		queryFn: () => getEventDocuments(eventId),
		enabled: !!event,
	});

	const { data: projectsData } = useQuery({
		queryKey: ['projects'],
		queryFn: () => getProjects(),
		enabled: usesSubEvents,
	});

	const { data: tagsData } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
		enabled: usesTags,
	});

	const types = typesData?.data || [];
	const approvals = approvalsData?.data || [];
	const documents = documentsData?.data || [];
	const tags = tagsData || [];

	const verifyPermission = authenticatedUser
		? getVerifyPermission(authenticatedUser.permissions)
		: () => false;

	const hasDocumentsForReview = documents.some((d) => d.status === 'inReview');
	const hasApprovalsTodo = approvals.find((t) => t.status === 'todo');

	useBreadcrumbLevel(0, 'Events', '/events');
	useBreadcrumbLevel(1, event?.name || 'Event');

	// Get current tab from pathname
	const pathSegments = location.pathname.split('/');
	const selectedLayoutSegment = pathSegments[pathSegments.length - 1] as
		| 'documents'
		| 'users'
		| 'logs'
		| 'details'
		| 'tasks'
		| 'approvals'
		| 'subcontracting'
		| 'groups';

	const { tabs, tabIndexPerTabId } = useMemo(() => {
		if (!event) return { tabs: [], tabIndexPerTabId: {} };

		const builtTabs = [
			...(event?.type?.Form && event.type.form !== 'event'
				? [{ id: 'details', label: 'Details' }]
				: []),
			...(verifyPermission([PERMISSIONS.adminUsersView])
				? [{ id: 'users', label: 'Users' }]
				: []),
			...(event?.projectId && usesSubEvents
				? [{ id: 'subcontracting', label: 'Subcontracting' }]
				: []),
			{ id: 'tasks', label: 'Tasks' },
			{ id: 'approvals', label: 'Approvals', error: !!hasApprovalsTodo },
			{ id: 'documents', label: 'Documents', error: hasDocumentsForReview },
			...(verifyPermission([PERMISSIONS.adminEventlogsView])
				? [{ id: 'logs', label: 'Logs' }]
				: []),
		];

		const tabIndexPerTabId = builtTabs.reduce(
			(acc, cur, index) => ({ ...acc, [cur.id]: index }),
			{} as Record<string, number>
		);

		return { tabs: builtTabs, tabIndexPerTabId };
	}, [event, verifyPermission, usesSubEvents, hasApprovalsTodo, hasDocumentsForReview]);

	const revalidateEventDetail = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['event', eventId] });
		queryClient.invalidateQueries({ queryKey: ['event-approvals', eventId] });
		queryClient.invalidateQueries({ queryKey: ['event-documents', eventId] });
	}, [queryClient, eventId]);

	const revalidateEvents = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['events'] });
	}, [queryClient]);

	const goBack = useCallback(() => {
		if (locallyStoredBackUrl) {
			try {
				const url = new URL(locallyStoredBackUrl);
				navigate({ to: url.pathname + url.search });
				return;
			} catch {
				// Invalid URL, fallback to default
			}
		}
		navigate({ to: '/events' });
	}, [locallyStoredBackUrl, navigate]);

	const handleEditEvent = async (formData: Record<string, unknown>) => {
		let isError = false;
		if (formData) {
			delete formData.eventData;
			await request
				.put(`/api/events/${eventId}`, formData)
				.then(() => {
					EventEmitter.emit('toast', { type: 'success', message: 'Event saved' });
				})
				.catch((error: { message?: string; body?: { message?: string } }) => {
					isError = true;
					const errorMessage =
						error?.message || error?.body?.message || 'Failed to save Event. Did you enter all data correctly?';
					EventEmitter.emit('toast', { type: 'error', message: errorMessage });
				});
		}

		revalidateEventDetail();
		revalidateEvents();
		if (!isError) {
			setIsFormOpened(false);
		}
	};

	const duplicateEvent = async (formData: Record<string, unknown>) => {
		let isError = false;
		if (formData) {
			await request
				.post('/api/events', formData)
				.then(({ body }: { body: { id: string } }) => {
					revalidateEvents();
					EventEmitter.emit('toast', { type: 'success', message: 'Event duplicated' });
					navigate({ to: '/events/$eventId', params: { eventId: body.id } });
				})
				.catch(() => {
					isError = true;
					EventEmitter.emit('toast', {
						type: 'error',
						message: 'Failed to duplicate event. Did you enter all data correctly?',
					});
				});
		}

		if (!isError) {
			setIsEventDuplicateDialogOpen(false);
		}
	};

	const restoreEvent = async () => {
		await request
			.put(`/api/events/${eventId}/restore`, {})
			.then(() => {
				EventEmitter.emit('toast', { type: 'success', message: 'Event restored' });
			})
			.catch(() => {
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to restore event.' });
			});
		revalidateEventDetail();
		revalidateEvents();
	};

	const toggleReview = async () => {
		if (!event) return;
		if (event.exception?.isException) {
			setIsReviewExceptionConfirmDialogOpen(true);
			return;
		}
		await request
			.put(`/api/events/${eventId}/set-review-status`, {
				markForReview: !event.markedForReview?.length,
			})
			.then(() => {
				EventEmitter.emit('toast', { type: 'success', message: 'Event saved' });
			})
			.catch(() => {
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to update review status' });
			});
		revalidateEventDetail();
		revalidateEvents();
	};

	const confirmReviewWithExceptionCancel = async () => {
		if (!event) return;
		setIsExceptionConfirmBusy(true);
		setExceptionActionPending(true);
		let isError = false;
		await request
			.put(`/api/events/${eventId}/set-review-status`, {
				markForReview: false,
			})
			.then(() => {
				EventEmitter.emit('toast', { type: 'success', message: 'Event reviewed, exception cancelled' });
			})
			.catch((error: { message?: string; body?: { message?: string } }) => {
				isError = true;
				const errorMessage =
					error?.message || error?.body?.message || 'Failed to update review status';
				EventEmitter.emit('toast', { type: 'error', message: errorMessage });
			});
		revalidateEventDetail();
		revalidateEvents();
		setIsExceptionConfirmBusy(false);
		setExceptionActionPending(false);
		if (!isError) {
			setIsReviewExceptionConfirmDialogOpen(false);
		}
	};

	const archiveEvent = async () => {
		try {
			await request.del(`/api/events/${eventId}`, {});
			revalidateEvents();
			setIsEventArchiveDialogOpen(false);
			goBack();
		} catch {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to archive event.' });
		}
	};

	const handleInitiateException = async () => {
		setIsInitiateExceptionSubmitting(true);
		let isError = false;
		await request
			.put(`/api/events/${eventId}/exception/initiate`, { reason: exceptionReason })
			.then(() => {
				EventEmitter.emit('toast', { type: 'success', message: 'Exception initiated' });
			})
			.catch(() => {
				isError = true;
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to initiate exception.' });
			});
		revalidateEventDetail();
		revalidateEvents();
		if (!isError) {
			setIsExceptionInitiateDialogOpen(false);
			setExceptionReason('');
		}
		setIsInitiateExceptionSubmitting(false);
	};

	const handleAcceptException = async () => {
		setIsAcceptExceptionSubmitting(true);
		let isError = false;
		await request
			.put(`/api/events/${eventId}/exception/accept`, { reason: exceptionAcceptReason })
			.then(() => {
				EventEmitter.emit('toast', { type: 'success', message: 'Exception accepted' });
			})
			.catch(() => {
				isError = true;
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to accept exception.' });
			});
		revalidateEventDetail();
		revalidateEvents();
		if (!isError) {
			setIsExceptionAcceptDialogOpen(false);
			setExceptionAcceptReason('');
		}
		setIsAcceptExceptionSubmitting(false);
	};

	const handleCancelException = async () => {
		setIsExceptionConfirmBusy(true);
		setExceptionActionPending(true);
		let isError = false;
		await request
			.put(`/api/events/${eventId}/exception/cancel`, {})
			.then(() => {
				EventEmitter.emit('toast', { type: 'success', message: 'Exception cancelled' });
			})
			.catch((error: { message?: string; body?: { message?: string } }) => {
				isError = true;
				const errorMessage = error?.message || error?.body?.message || 'Failed to cancel exception.';
				EventEmitter.emit('toast', { type: 'error', message: errorMessage });
			});
		revalidateEventDetail();
		revalidateEvents();
		setIsExceptionConfirmBusy(false);
		setExceptionActionPending(false);
		if (!isError) {
			setIsExceptionCancelDialogOpen(false);
		}
	};

	const handleTabChange = (tabIndex: number) => {
		if (tabs[tabIndex]) {
			navigate({ to: `/events/$eventId/${tabs[tabIndex].id}`, params: { eventId } });
		}
	};

	const isLoading = authLoading || configLoading || eventLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!event) {
		return (
			<GeneralNotFound
				label="We couldn't find this event. Maybe it has been deleted?"
				action={{
					label: 'Return to events',
					href: '/events',
					variant: 'outlined',
				}}
			/>
		);
	}

	return (
		<>
			<GenericDetailPage>
				<GenericDetailPage.Content>
					<GenericDetailPage.Content.Notifications>
						{event.archived && (
							<Alert
								severity="info"
								variant="filled"
								action={
									verifyPermission([PERMISSIONS.adminEventsArchive]) && (
										<Button color="inherit" onClick={restoreEvent}>
											Restore
										</Button>
									)
								}
							>
								This event is archived.
							</Alert>
						)}
						{hasApprovalsTodo && (
							<Alert
								severity="warning"
								variant="filled"
								sx={{ marginBottom: 2, '& ul': { margin: 0, paddingLeft: 2 } }}
							>
								This event has one or more approval tasks pending approval.
							</Alert>
						)}
						{event.status === 'inReview' && (
							<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
								Event needs to be reviewed before allowing access
								<ul>
									{event?.requirements?.map((r) =>
										r.issues?.map((i, idx) => <li key={idx}>{i}</li>)
									)}
								</ul>
							</Alert>
						)}
						{event.status === 'exception' && (
							<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
								Exception procedure active — access is denied until the exception is accepted.
								{event.exception?.reason && (
									<>
										<br />
										Reason: {event.exception.reason}
									</>
								)}
							</Alert>
						)}
						{event.exception?.isException && event.status !== 'exception' && (
							<Alert severity="warning" variant="filled">
								This event was accepted via the exception procedure.
								{event.exception?.reason && (
									<>
										<br />
										Exception reason: {event.exception.reason}
									</>
								)}
								{event.exception?.acceptReason && (
									<>
										<br />
										Accept reason: {event.exception.acceptReason}
									</>
								)}
							</Alert>
						)}
					</GenericDetailPage.Content.Notifications>
					<EventInfoBox
						event={event}
						exceptionActionPending={exceptionActionPending}
						permissions={{
							canEdit: verifyPermission([PERMISSIONS.adminEventsEdit]),
							canArchive: verifyPermission([PERMISSIONS.adminEventsArchive]),
							canDuplicate: verifyPermission([PERMISSIONS.adminEventsCreate]),
							canReview: verifyPermission([PERMISSIONS.adminEventsReview]),
						}}
						openEditForm={() => setIsFormOpened(true)}
						openArchiveDialog={() => setIsEventArchiveDialogOpen(true)}
						openDuplicateDialog={() => setIsEventDuplicateDialogOpen(true)}
						openReviewDialog={toggleReview}
						openExceptionDialog={verifyPermission([PERMISSIONS.adminEventsException]) ? () => setIsExceptionInitiateDialogOpen(true) : undefined}
						openAcceptExceptionDialog={verifyPermission([PERMISSIONS.adminEventsException]) ? () => setIsExceptionAcceptDialogOpen(true) : undefined}
						openCancelExceptionDialog={
							verifyPermission([PERMISSIONS.adminEventsException]) ? () => setIsExceptionCancelDialogOpen(true) : undefined
						}
						usesSubEvents={usesSubEvents}
					/>
				</GenericDetailPage.Content>
				<Tabs
					value={tabIndexPerTabId[selectedLayoutSegment] ?? 0}
					onChange={handleTabChange}
					className="px-4"
				>
					<Tabs.TabList>
						{tabs.map((tab, index) => (
							<Tabs.Tab
								key={tab.id}
								value={index}
								label={
									<div className="flex items-center gap-2">
										{tab.label}
										{tab.error && <span className="inline-flex h-2 w-2 rounded-full bg-red-600" />}
									</div>
								}
							/>
						))}
					</Tabs.TabList>
				</Tabs>
				<Outlet />
			</GenericDetailPage>

			<ConfirmDialog
				isOpen={isEventArchiveDialogOpen}
				cancelAction={() => setIsEventArchiveDialogOpen(false)}
				confirmAction={archiveEvent}
				cancelLabel="Cancel"
				confirmLabel="Archive"
			>
				Are you sure you want to archive this event?
			</ConfirmDialog>

			<DuplicateEventDialog
				isOpen={isEventDuplicateDialogOpen}
				onClose={() => setIsEventDuplicateDialogOpen(false)}
				onSubmit={duplicateEvent}
				event={event}
				types={types}
				singleGroupAsOwner={usesSubEvents}
				usesSubEvents={usesSubEvents}
			/>

			<EventForm
				isOpen={isFormOpened}
				data={{ ...event }}
				onClose={() => setIsFormOpened(false)}
				onSubmit={handleEditEvent}
				types={types}
				tags={tags}
				singleGroupAsOwner={usesSubEvents}
				usesSubEvents={usesSubEvents}
				usesTags={usesTags}
			/>

			<Dialog
				open={isExceptionInitiateDialogOpen}
				onClose={(_e, reason) => {
					if (isInitiateExceptionSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
					setIsExceptionInitiateDialogOpen(false);
					setExceptionReason('');
				}}
				disableEscapeKeyDown={isInitiateExceptionSubmitting}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Initiate Exception Procedure</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="Reason for exception"
						fullWidth
						multiline
						rows={3}
						value={exceptionReason}
						onChange={(e) => setExceptionReason(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setIsExceptionInitiateDialogOpen(false);
							setExceptionReason('');
						}}
						disabled={isInitiateExceptionSubmitting}
					>
						Cancel
					</Button>
					<LoadingButton
						onClick={handleInitiateException}
						variant="contained"
						disabled={!exceptionReason.trim()}
						loading={isInitiateExceptionSubmitting}
					>
						Confirm
					</LoadingButton>
				</DialogActions>
			</Dialog>

			<Dialog
				open={isExceptionAcceptDialogOpen}
				onClose={(_e, reason) => {
					if (isAcceptExceptionSubmitting && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
					setIsExceptionAcceptDialogOpen(false);
					setExceptionAcceptReason('');
				}}
				disableEscapeKeyDown={isAcceptExceptionSubmitting}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Accept Exception</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="Reason for accepting"
						fullWidth
						multiline
						rows={3}
						value={exceptionAcceptReason}
						onChange={(e) => setExceptionAcceptReason(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setIsExceptionAcceptDialogOpen(false);
							setExceptionAcceptReason('');
						}}
						disabled={isAcceptExceptionSubmitting}
					>
						Cancel
					</Button>
					<LoadingButton
						onClick={handleAcceptException}
						variant="contained"
						disabled={!exceptionAcceptReason.trim()}
						loading={isAcceptExceptionSubmitting}
					>
						Confirm
					</LoadingButton>
				</DialogActions>
			</Dialog>

			<ConfirmDialog
				isOpen={isExceptionCancelDialogOpen}
				cancelAction={() => setIsExceptionCancelDialogOpen(false)}
				confirmAction={handleCancelException}
				cancelLabel="No"
				confirmLabel="Yes, cancel exception"
				confirmDisabled={isExceptionConfirmBusy}
			>
				Are you sure you want to cancel the exception procedure? The event status will be determined by the normal review flow.
			</ConfirmDialog>

			<ConfirmDialog
				isOpen={isReviewExceptionConfirmDialogOpen}
				cancelAction={() => setIsReviewExceptionConfirmDialogOpen(false)}
				confirmAction={confirmReviewWithExceptionCancel}
				cancelLabel="Cancel"
				confirmLabel="Yes, mark reviewed"
				confirmDisabled={isExceptionConfirmBusy}
			>
				This event is currently marked as an exception. Marking it as reviewed will cancel the exception procedure. The event will return to the normal flow, meaning that if a new review reason arises in the future, the event can automatically be placed back in review.
			</ConfirmDialog>
		</>
	);
}
